import { Trans } from '@lingui/macro';
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { find, get, isEmpty, isNil } from 'lodash';
import {
    ID_MAPPING,
    INFRASTRUCTURE_COLLECT_LIST_DATA_TABLE_ID,
    NAME_FIELD,
    NO_PAGING_ROW_LIMIT,
} from '../InfrastructureConstants';
import { useGroupAdminStyles } from '../../groups/GroupsAdminUtils';
import { useDataService } from '../../../../services/ApiDataService';
import { useAsyncDataFetch } from '../../../../utilities/Hooks';
import { EMPTY_BUTTONS } from '../../../../services/FormDataTableActionService';
import FormSection from '../../../form/FormSection';
import ListActionSelector from '../../../form/ListActionSelector';
import { SELECTIONMODE_SINGLE } from '../../../data-table/SelectionContext';
import {
    COLLECTOR_INITIAL_SORT_FIELD_INFO,
    COLLECTOR_LIST_COLUMNS,
    COLLECTOR_LIST_REQUEST_PARAMS,
    COLLECTOR_LIST_SEARCH_FIELDS,
    COLLECTOR_LIST_URL,
} from '../../collectors/CollectorsAdminConstants';
import { ErrorSpanMessage } from '../../../form/ValidationError';
import { getSystemTime } from '../../../../stores/SystemTimeStore';

const INITIAL_LOAD_TIME = -1;
const EMPTY_STATE_MESSAGE = 'No collector available';
const FIRST_ITEM_INDEX = 0;

/**
 * Displays instructional text based on collector selection state
 * @param {Object} props - Component props
 * @param {boolean} props.hasSelection - Whether a collector is currently selected
 * @returns {JSX.Element} Instructional or error message
 */
const SubText = ({ hasSelection }) => {
    if (!hasSelection) {
        return <ErrorSpanMessage message={<Trans>No nGeniusPULSE Collector</Trans>} />;
    }
    return <Trans>Select the nGeniusPULSE Collector to monitor this vCenter</Trans>;
};

SubText.propTypes = {
    hasSelection: PropTypes.bool.isRequired,
};

/**
 * Creates a collector selection object
 * @param {string} collectorId - Collector ID
 * @param {string} collectorName - Collector name
 * @returns {Object} Selection object with ID and name fields
 */
const createCollectorSelection = (collectorId, collectorName) => {
    if (isNil(collectorId) || isEmpty(collectorName)) {
        return null;
    }
    return {
        [ID_MAPPING]: collectorId,
        [NAME_FIELD]: collectorName,
    };
};

/**
 * Displays a list of nGeniusPULSE Collectors for vCenter monitoring
 * Provides single-selection interface with save button state management
 */
const CollectorListSelector = ({ selectionCollectorRef, setSaveButtonDisabled }) => {
    const styles = useGroupAdminStyles();
    const dataService = useDataService();
    const [lastLoadDataArrayTime, setLastLoadDataArrayTime] = useState(INITIAL_LOAD_TIME);
    const [hasSelection, setHasSelection] = useState(false);

    const fetchCollectorList = useCallback(
        () => dataService.get(COLLECTOR_LIST_URL, COLLECTOR_LIST_REQUEST_PARAMS),
        [dataService]
    );

    const handleFetchSuccess = useCallback(
        (collectors) => {
            const hasNoCollectors = isEmpty(collectors);
            setSaveButtonDisabled(hasNoCollectors);
            setLastLoadDataArrayTime(getSystemTime().valueOf());
        },
        [setSaveButtonDisabled]
    );

    const handleFetchError = useCallback(() => {
        setSaveButtonDisabled(true);
    }, [setSaveButtonDisabled]);

    const { data: collectors, emptyText } = useAsyncDataFetch({
        fetchFn: fetchCollectorList,
        emptyStateMessage: EMPTY_STATE_MESSAGE,
        onSuccess: handleFetchSuccess,
        onError: handleFetchError,
        dependencies: [fetchCollectorList],
    });

    const updateCollectorSelection = useCallback(
        (collectorId, collectorName) => {
            const selection = createCollectorSelection(collectorId, collectorName);
            if (isNil(selection)) {
                setSaveButtonDisabled(true);
                selectionCollectorRef.current = undefined;
                setHasSelection(false);
                return;
            }

            setSaveButtonDisabled(false);
            selectionCollectorRef.current = selection;
            setHasSelection(true);
        },
        [setSaveButtonDisabled, selectionCollectorRef]
    );

    const clearCollectorSelection = useCallback(() => {
        setSaveButtonDisabled(true);
        selectionCollectorRef.current = undefined;
        setHasSelection(false);
    }, [setSaveButtonDisabled, selectionCollectorRef]);

    const isSelectionUnchanged = useCallback(
        (newSelectionId) => {
            const currentSelectionId = get(selectionCollectorRef, ['current', ID_MAPPING]);
            return newSelectionId === currentSelectionId;
        },
        [selectionCollectorRef]
    );

    const findCollectorById = useCallback(
        (collectorId) => {
            if (isNil(collectorId) || isEmpty(collectors)) {
                return null;
            }
            return find(collectors, { [ID_MAPPING]: collectorId });
        },
        [collectors]
    );

    const handleSelectionChange = useCallback(
        (tableSelection) => {
            const selectionIds = get(tableSelection, 'selection', []);

            if (isEmpty(selectionIds)) {
                clearCollectorSelection();
                return;
            }

            const selectedId = selectionIds[FIRST_ITEM_INDEX];

            if (isSelectionUnchanged(selectedId)) {
                return;
            }

            const selectedCollector = findCollectorById(selectedId);

            if (isNil(selectedCollector)) {
                clearCollectorSelection();
                return;
            }

            const collectorName = get(selectedCollector, 'name', '');
            updateCollectorSelection(selectedId, collectorName);
        },
        [clearCollectorSelection, isSelectionUnchanged, findCollectorById, updateCollectorSelection]
    );

    const subText = useMemo(() => <SubText hasSelection={hasSelection} />, [hasSelection]);

    return (
        <FormSection title={<Trans>Collectors</Trans>} subText={subText}>
            <div className={styles.selectorWrapper}>
                <ListActionSelector
                    tableId={INFRASTRUCTURE_COLLECT_LIST_DATA_TABLE_ID}
                    model={SELECTIONMODE_SINGLE}
                    gridColumns={COLLECTOR_LIST_COLUMNS}
                    sorters={COLLECTOR_INITIAL_SORT_FIELD_INFO}
                    dataArray={collectors}
                    lastLoadDataArrayTime={lastLoadDataArrayTime}
                    dataArraySearchFields={COLLECTOR_LIST_SEARCH_FIELDS}
                    actionButtons={EMPTY_BUTTONS}
                    emptyText={emptyText}
                    changeSelectionId={handleSelectionChange}
                    visibleRows={NO_PAGING_ROW_LIMIT}
                />
            </div>
        </FormSection>
    );
};

CollectorListSelector.propTypes = {
    setSaveButtonDisabled: PropTypes.func,
    selectionCollectorRef: PropTypes.shape({
        current: PropTypes.object,
    }),
};

CollectorListSelector.defaultProps = {
    setSaveButtonDisabled: () => {},
    selectionCollectorRef: { current: null },
};

export default CollectorListSelector;
